import { useState } from "react"
import { getTableConfig } from "../../../../components/ModularDataTable/utils/persistConfig"
import usePluginsSpace from "../../../../hooks/usePluginSpace"
import { POST } from "../../../../services/api"
import { customerMapper } from "./customerMapper"


type FilterCustomerDto = {
    name: string
    doc: string
    email: string
    status: string
    customerGroupId: string
}

export function useCustomer(persistPageKey_?: string, persistContextKey_?: string) {
    const persistPageKey = persistPageKey_ || 'customers'
    const persistContextKey = persistContextKey_ || 'default'

    const { pluginSpace } = usePluginsSpace()
    const isWholeSaler = Boolean(pluginSpace?.wholeSaler)

    const [customers, setCustomers] = useState<any[]>([])
 
    const [tableLoading, setTableLoading] = useState(false)

    // Carrega persistido
    const persisted: any = getTableConfig(persistPageKey, persistContextKey) || {} as FilterCustomerDto
    const [customerFilters, setCustomerFilters] = useState<FilterCustomerDto>({} as FilterCustomerDto)

    const handleFilterChange = async (newFilters: FilterCustomerDto) => {
        setCustomerFilters(newFilters)
    }

    const [pagination, setPagination] = useState({
        page: persisted.pagination?.page || 1,
        pageSize: persisted.pagination?.pageSize || 10,
        total: 0,
        pageSizes: [10, 25, 50, 100],
    })
    const [sorting, setSorting] = useState({
        field: persisted.sorting?.field || 'id',
        direction: persisted.sorting?.direction || 'asc',
    })
    const [searchTerm, setSearchTerm] = useState(persisted.filters?.search || '')

    const handleDataChange = async (params: {
        page: number
        pageSize: number
        search: string
        sorting?: { field: string; direction: 'asc' | 'desc' }
        filters?: any
    }) => {
        setPagination((prev) => ({ ...prev, page: params.page, pageSize: params.pageSize }))
        if (params.sorting) setSorting(params.sorting)
        setSearchTerm(params.search)
        if (params.filters) setCustomerFilters(params.filters)
        return await getCustomers(params)
    }

    const handlePaginationChange = async (newPagination: any) => {
        setPagination(newPagination)
        return await getCustomers({
            page: newPagination.page,
            pageSize: newPagination.pageSize,
            search: searchTerm,
            sorting: sorting.field ? sorting : undefined,
        })
    }

    const handleSortingChange = async (newSorting: any) => {
        setSorting(newSorting)
        return await getCustomers({
            page: pagination.page,
            pageSize: pagination.pageSize,
            search: searchTerm,
            sorting: newSorting,
        })
    }

    async function getCustomers(params: {
        page: number
        pageSize: number
        search: string
        sorting?: { field: string; direction: 'asc' | 'desc' }
        filters?: any
    }) {
        try {
            setTableLoading(true)
            const currentPage = params?.page || pagination.page
            const currentPageSize = params?.pageSize || pagination.pageSize
            const currentSearch = params?.search || searchTerm
            const currentSorting = params?.sorting || sorting
            const skip = (currentPage - 1) * currentPageSize

            const query = `?field=${currentSorting.field}&order=${currentSorting.direction.toUpperCase()}&page=${currentPage}&take=${currentPageSize}&search=${currentSearch}&skip=${skip}`

            const request = !isWholeSaler
                ? await POST(`/customer/filter${query}`, { internal: true, ...customerFilters })
                : await POST(`/customer/filter_whole_saller${query}`, { internal: true, ...customerFilters })
            const customers = request.data.map((row: any) => customerMapper(row))

            setCustomers(customers)

            if (request.meta) {
                const paginagionInfo = {
                    total: request.meta.itemCount,
                    page: currentPage,
                    pageSize: currentPageSize,
                }
                setPagination((prev) => ({
                    ...prev,
                    ...paginagionInfo,
                }))
            }
        } catch (error) {
            console.error('Error fetching customers:', error)
            setCustomers([]) // Clear customers on error
        } finally {
            setTableLoading(false)
        }

    }

    async function getInitialCustomers() {

        await getCustomers({
            page: 1,
            pageSize: pagination.pageSize,
            search: searchTerm,
            sorting: sorting.field ? sorting : undefined,
            filters: null,
        })
    }

    return {
        customers,
        tableLoading,
        customerFilters,
        pagination,
        sorting,
        searchTerm,
        handleDataChange,
        handlePaginationChange,
        handleSortingChange,
        getCustomers,
        getInitialCustomers,
        handleFilterChange,
    }

}